import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { FooterLinksSection, FooterLinksSectionBaseProps } from './FooterLinksSection';
import { FooterMobileStoresBaseProps } from './AdditionalFooterSections/FooterMobileStores';
import { useTheme } from '../../../../theming/useTheme';
import { SocialSectionDataProps } from './AdditionalFooterSections/FooterSocialLinks';
import { generateSeparatorSections } from './footer.utils';
import { AdditionalFooterSections } from './AdditionalFooterSections/AdditionalFooterSections';
import { ErrorBoundary } from '../../../errorHandling/ErrorBoundary';
import { BottomFooterBar, BottomFooterBarDataProps } from './BottomFooterBar';
import { MEDIA_BREAKPOINTS } from '../../../../mediaQueries.const';
import { BaseUnit } from '../../../../theming/baseUnitDefinition';

const componentConfigurationKey = 'footer';

interface FooterDataProps {
  linksSection: FooterLinksSectionBaseProps | null,
  socialSection: SocialSectionDataProps | null,
  appsSection: FooterMobileStoresBaseProps | null,
  bottomFooterBar: BottomFooterBarDataProps
}

interface FooterComponentThemeProps {
  backgroundColor: string,
  borderTopColor: string,
  separatorColor: string,
  baseUnit: BaseUnit,
}

type FooterComponentProps = FooterDataProps & FooterComponentThemeProps;

const getFooterStyle = (theme: FooterComponentThemeProps) => {
  const { baseUnit, borderTopColor, backgroundColor } = theme;
  return StyleSheet.create({
    footer: {
      backgroundColor,
      borderTop: `solid 2px ${borderTopColor}`,
      [MEDIA_BREAKPOINTS.LARGE]: {
        padding: `0 ${baseUnit}px`,
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        padding: `0 ${baseUnit}px`,
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        padding: `0 ${baseUnit * 0.75}px`,
      },
    },
    footerContent: {
      margin: 'auto',
      [MEDIA_BREAKPOINTS.LARGE]: {
        maxWidth: '852px',
      },
    },
  });
};

const getSeparatorStyle = (separatorColor: string) => {
  return StyleSheet.create({
    style: {
      height: '1px',
      backgroundColor: separatorColor,
    },
  });
};

const getLinksSection = (linksSection: FooterLinksSectionBaseProps | null) => {
  return linksSection ? <FooterLinksSection key="footer-links" {...linksSection as FooterLinksSectionBaseProps} /> : null;
};

const getAdditionalFooterSection = (socialSection: SocialSectionDataProps | null, appsSection: FooterMobileStoresBaseProps | null, separatorColor: string) => {
  if (socialSection === null && appsSection === null) return null;
  return <AdditionalFooterSections key="additional-links" socialSection={socialSection} appsSection={appsSection} separatorColor={separatorColor} />;
};

const getFooterItems = (linksSection: FooterLinksSectionBaseProps | null, socialSection: SocialSectionDataProps | null, appsSection: FooterMobileStoresBaseProps | null, separatorColor: string) => {
  return [
    getLinksSection(linksSection),
    getAdditionalFooterSection(socialSection, appsSection, separatorColor),
  ]
    .filter(section => section !== null)
    .reduce(generateSeparatorSections(getSeparatorStyle(separatorColor).style), []);
};

export const FooterComponent: React.FunctionComponent<FooterComponentProps> = props => {
  const footerComponentStyle = getFooterStyle(props);
  const { linksSection, socialSection, appsSection, separatorColor, bottomFooterBar } = props;
  return (
    <footer>
      <div className={css(footerComponentStyle.footer)}>
        <div className={css(footerComponentStyle.footerContent)}>
          {getFooterItems(linksSection, socialSection, appsSection, separatorColor)}
        </div>
      </div>
      <BottomFooterBar {...bottomFooterBar} />
    </footer>
  );
};

const mapThemeProps = (theme: FooterComponentThemeProps) => {
  return {
    backgroundColor: theme.backgroundColor,
    borderTopColor: theme.borderTopColor,
    separatorColor: theme.separatorColor,
    baseUnit: theme.baseUnit,
  };
};

export const Footer = (props: FooterDataProps) => {
  const themeContext = useTheme(mapThemeProps, componentConfigurationKey);
  return <ErrorBoundary><FooterComponent {...themeContext} {...props} /></ErrorBoundary>;
};
